/** Filas mínimas de Libro IVA Ventas para el informe IVA Simple */
export interface IvaSimpleVentaRow {
  tipoComprobante: string;
  tipoIva: string;
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  total: number;
}

/** Filas mínimas de Libro IVA Compras para el informe IVA Simple */
export interface IvaSimpleCompraRow {
  tipoComprobante: string;
  tipoIva: string;
  netoGravado: number;
  iva21: number;
  iva105: number;
  iva27: number;
  exento: number;
  total: number;
}

export interface IvaSimpleAlicuotaLinea {
  alicuota: number;
  neto: number;
  iva: number;
  total: number;
}

export interface IvaSimpleCategoria {
  etiqueta: string;
  lineas: IvaSimpleAlicuotaLinea[];
  /** Monto único para operaciones solo exentas (sin desglose neto/IVA). */
  montoExento?: number | null;
  esSubseccion?: boolean;
}

export interface IvaSimpleBloqueTotales {
  neto: number;
  iva: number;
  total: number;
}

export interface IvaSimpleSeccion {
  titulo: string;
  subtitulo: string;
  categorias: IvaSimpleCategoria[];
  total: IvaSimpleBloqueTotales;
}

export interface InformeIvaSimple {
  ventas: IvaSimpleSeccion;
  compras: IvaSimpleSeccion;
}

interface AlicuotaAcum {
  neto: number;
  iva: number;
}

const ALICUOTAS = [
  { key: '21' as const, tasa: 21, divisor: 0.21 },
  { key: '105' as const, tasa: 10.5, divisor: 0.105 },
  { key: '27' as const, tasa: 27, divisor: 0.27 },
] as const;

type AlicuotaKey = (typeof ALICUOTAS)[number]['key'];

const vacioAlicuotas = (): Record<AlicuotaKey, AlicuotaAcum> => ({
  '21': { neto: 0, iva: 0 },
  '105': { neto: 0, iva: 0 },
  '27': { neto: 0, iva: 0 },
});

const round2 = (n: number) => Math.round(n * 100) / 100;

export function esNotaCreditoComprobante(tipoComprobante: string, total?: number): boolean {
  const t = (tipoComprobante ?? '').toUpperCase();
  if (total != null && total < 0) return true;
  return (
    (t.includes('NOTA') && (t.includes('CRÉDITO') || t.includes('CREDITO')))
    || t.includes('NOTA DE CR')
    || /\bNC\b/.test(t)
  );
}

/** Signo para acumular NC: resta del fiscal aunque el API devuelva importes positivos. */
function signoNotaCredito(row: { total: number; iva21: number; iva105: number; iva27: number }): number {
  const ref = row.total || row.iva21 || row.iva105 || row.iva27;
  return ref < 0 ? 1 : -1;
}

function acumularAlicuotas(
  dest: Record<AlicuotaKey, AlicuotaAcum>,
  row: { netoGravado: number; iva21: number; iva105: number; iva27: number },
  signo: number
) {
  const absIva21 = Math.abs(row.iva21);
  const absIva105 = Math.abs(row.iva105);
  const absIva27 = Math.abs(row.iva27);
  const totalIvaAbs = absIva21 + absIva105 + absIva27;
  const netoSigned = row.netoGravado * signo;

  if (row.iva21) {
    dest['21'].iva = round2(dest['21'].iva + row.iva21 * signo);
    const netoPart =
      totalIvaAbs > 0 ? round2(netoSigned * (absIva21 / totalIvaAbs)) : netoSigned;
    dest['21'].neto = round2(dest['21'].neto + netoPart);
  }
  if (row.iva105) {
    dest['105'].iva = round2(dest['105'].iva + row.iva105 * signo);
    const netoPart =
      totalIvaAbs > 0 ? round2(netoSigned * (absIva105 / totalIvaAbs)) : 0;
    dest['105'].neto = round2(dest['105'].neto + netoPart);
  }
  if (row.iva27) {
    dest['27'].iva = round2(dest['27'].iva + row.iva27 * signo);
    const netoPart =
      totalIvaAbs > 0 ? round2(netoSigned * (absIva27 / totalIvaAbs)) : 0;
    dest['27'].neto = round2(dest['27'].neto + netoPart);
  }
}

/** Totales de ventas alineados con el pie del Libro IVA Ventas (suma directa de columnas). */
export function totalesVentasLibroIvaDesdeFilas(rows: IvaSimpleVentaRow[]): IvaSimpleBloqueTotales {
  return rows.reduce(
    (acc, row) => ({
      neto: round2(acc.neto + row.netoGravado),
      iva: round2(acc.iva + row.iva21 + row.iva105 + row.iva27),
      total: round2(acc.total + row.total),
    }),
    { neto: 0, iva: 0, total: 0 }
  );
}

function alicuotasALineas(acum: Record<AlicuotaKey, AlicuotaAcum>): IvaSimpleAlicuotaLinea[] {
  const lineas: IvaSimpleAlicuotaLinea[] = [];
  for (const { key, tasa } of ALICUOTAS) {
    const { neto, iva } = acum[key];
    if (!neto && !iva) continue;
    const total = round2(neto + iva);
    lineas.push({ alicuota: tasa, neto, iva, total });
  }
  return lineas;
}

function categoriaConAlicuotas(
  etiqueta: string,
  acum: Record<AlicuotaKey, AlicuotaAcum>,
  esSubseccion = false
): IvaSimpleCategoria | null {
  const lineas = alicuotasALineas(acum);
  if (!lineas.length) return null;
  return { etiqueta, lineas, esSubseccion };
}

function esPrestacionServicios(tipoComprobante: string): boolean {
  const t = (tipoComprobante ?? '').toUpperCase();
  return t.includes('SERVIC') || t.includes('HONORAR') || t.includes('LOCAC');
}

/** Totales de compras alineados con el pie del Libro IVA Compras (suma directa de columnas). */
export function totalesComprasLibroIvaDesdeFilas(rows: IvaSimpleCompraRow[]): IvaSimpleBloqueTotales {
  return rows.reduce(
    (acc, row) => ({
      neto: round2(acc.neto + row.netoGravado),
      iva: round2(acc.iva + row.iva21 + row.iva105 + row.iva27),
      total: round2(acc.total + row.total),
    }),
    { neto: 0, iva: 0, total: 0 }
  );
}

export function calcularDiferenciaIvaSimple(informe: InformeIvaSimple): IvaSimpleBloqueTotales {
  return {
    neto: round2(informe.ventas.total.neto - informe.compras.total.neto),
    iva: round2(informe.ventas.total.iva - informe.compras.total.iva),
    total: round2(informe.ventas.total.total - informe.compras.total.total),
  };
}

export function buildInformeIvaSimple(
  ventas: IvaSimpleVentaRow[],
  compras: IvaSimpleCompraRow[]
): InformeIvaSimple {
  const ventasRi = vacioAlicuotas();
  const ventasCf = vacioAlicuotas();
  const ventasRestCred = vacioAlicuotas();
  let ventasExento = 0;

  ventas.forEach((row) => {
    const esNc = esNotaCreditoComprobante(row.tipoComprobante, row.total);

    if (esNc) {
      acumularAlicuotas(ventasRestCred, row, signoNotaCredito(row));
      return;
    }

    if (row.tipoIva === 'Resp. Insc.') {
      ventasExento = round2(ventasExento + row.exento);
      acumularAlicuotas(ventasRi, row, 1);
      return;
    }

    if (row.tipoIva === 'Cons. Final' || row.tipoIva === 'Monotributo') {
      ventasExento = round2(ventasExento + row.exento);
      acumularAlicuotas(ventasCf, row, 1);
      return;
    }

    if (row.tipoIva === 'Exento') {
      ventasExento = round2(ventasExento + (row.exento || row.netoGravado || row.total));
      return;
    }

    if (row.tipoIva === 'No Categorizado') {
      if (!row.iva21 && !row.iva105 && !row.iva27) {
        ventasExento = round2(ventasExento + (row.exento || row.netoGravado || row.total));
        return;
      }
      ventasExento = round2(ventasExento + row.exento);
      acumularAlicuotas(ventasRi, row, 1);
      return;
    }

    ventasExento = round2(ventasExento + row.exento);
    acumularAlicuotas(ventasRi, row, 1);
  });

  const ventasCategorias: IvaSimpleCategoria[] = [];
  const catRi = categoriaConAlicuotas('Operaciones con Responsables Inscriptos', ventasRi);
  if (catRi) ventasCategorias.push(catRi);

  const catCf = categoriaConAlicuotas('Operaciones con Consumidores Finales', ventasCf);
  if (catCf) ventasCategorias.push(catCf);

  if (ventasExento) {
    ventasCategorias.push({
      etiqueta: 'Operaciones No Gravadas y Exentas',
      lineas: [],
      montoExento: ventasExento,
    });
  }

  const catRestCred = categoriaConAlicuotas('Restitución de Creditos Fiscales', ventasRestCred);
  if (catRestCred) ventasCategorias.push(catRestCred);

  const comprasBienes = vacioAlicuotas();
  const comprasServicios = vacioAlicuotas();
  const comprasOtros = vacioAlicuotas();
  const comprasRestDebRi = vacioAlicuotas();
  const comprasRestDebCf = vacioAlicuotas();
  let comprasExento = 0;
  let comprasNoInscripto = 0;
  let comprasMonotributo = 0;
  let comprasExentoProv = 0;

  compras.forEach((row) => {
    const esNc = esNotaCreditoComprobante(row.tipoComprobante, row.total);
    const signoNc = signoNotaCredito(row);

    if (esNc) {
      if (row.tipoIva === 'Cons. Final' || row.tipoIva === 'Monotributo') {
        acumularAlicuotas(comprasRestDebCf, row, signoNc);
      } else {
        acumularAlicuotas(comprasRestDebRi, row, signoNc);
      }
      return;
    }

    const signo = 1;

    if (row.tipoIva === 'Monotributo') {
      comprasMonotributo = round2(comprasMonotributo + row.total * signo);
      return;
    }

    if (row.tipoIva === 'Exento') {
      comprasExentoProv = round2(comprasExentoProv + row.total * signo);
      return;
    }

    if (row.tipoIva === 'No Categorizado') {
      if (!row.iva21 && !row.iva105 && !row.iva27) {
        comprasNoInscripto = round2(comprasNoInscripto + row.total * signo);
        return;
      }
      acumularAlicuotas(comprasOtros, row, signo);
      return;
    }

    if (!row.iva21 && !row.iva105 && !row.iva27) {
      comprasExento = round2(comprasExento + (row.exento || row.total) * signo);
      return;
    }

    if (esPrestacionServicios(row.tipoComprobante)) {
      acumularAlicuotas(comprasServicios, row, signo);
    } else {
      acumularAlicuotas(comprasBienes, row, signo);
    }
  });

  const comprasCategorias: IvaSimpleCategoria[] = [];
  const catBienes = categoriaConAlicuotas('Por compras de bienes en el mercado local', comprasBienes);
  if (catBienes) comprasCategorias.push(catBienes);

  const catServ = categoriaConAlicuotas('Por Prestaciones de Servicios', comprasServicios);
  if (catServ) comprasCategorias.push(catServ);

  const catOtros = categoriaConAlicuotas('Por Otros conceptos', comprasOtros);
  if (catOtros) comprasCategorias.push(catOtros);

  const catRestRi = categoriaConAlicuotas('Operaciones con Responsables Inscriptos', comprasRestDebRi, true);
  const catRestCf = categoriaConAlicuotas('Operaciones con Consumidores Finales', comprasRestDebCf, true);
  if (catRestRi) comprasCategorias.push(catRestRi);
  if (catRestCf) comprasCategorias.push(catRestCf);

  if (comprasExento) {
    comprasCategorias.push({
      etiqueta: 'Compras No Gravadas y Exentas',
      lineas: [],
      montoExento: comprasExento,
    });
  }
  if (comprasNoInscripto) {
    comprasCategorias.push({
      etiqueta: 'Compras a Responsables No Inscriptos',
      lineas: [],
      montoExento: comprasNoInscripto,
    });
  }
  if (comprasMonotributo) {
    comprasCategorias.push({
      etiqueta: 'Compras a Monotributistas',
      lineas: [],
      montoExento: comprasMonotributo,
    });
  }
  if (comprasExentoProv) {
    comprasCategorias.push({
      etiqueta: 'Compras a Exentos',
      lineas: [],
      montoExento: comprasExentoProv,
    });
  }

  return {
    ventas: {
      titulo: 'VENTAS',
      subtitulo: 'Debito Fiscal',
      categorias: ventasCategorias,
      total: totalesVentasLibroIvaDesdeFilas(ventas),
    },
    compras: {
      titulo: 'COMPRAS',
      subtitulo: 'Credito Fiscal',
      categorias: comprasCategorias,
      total: totalesComprasLibroIvaDesdeFilas(compras),
    },
  };
}
